import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NoData from "../common/NoData";
import InitialsAvatar from "../../util/name_initial";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import EditPlayerMutation from "./mutations/EditPlayer";
import DeletePlayerMutation from "./mutations/DeletePlayer";
import { selectActiveWebProfileId } from "../../store/selectors";
import { confirmDelete } from "../common/confirmDelete";

export default function PlayersList({ team, colors, division, onPlayerUpdated, onAddPlayer, onPlayerDeleted }) {
    const { t, getRaw } = useTranslation();
    const activeProfileId = useSelector(selectActiveWebProfileId);
    const [editPlayer] = EditPlayerMutation();
    const [deletePlayer] = DeletePlayerMutation();

    const [players, setPlayers] = useState(team?.team?.players || []);
    const [editingRosterId, setEditingRosterId] = useState(null);
    const [draftShirtNumber, setDraftShirtNumber] = useState("");
    const [draftPosition, setDraftPosition] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [deletingRosterId, setDeletingRosterId] = useState(null);

    useEffect(() => {
        setPlayers(team?.team?.players || []);
    }, [team?.team?.players]);

    const sportKey = division?.sport?.key;
    const positionOptions = useMemo(() => {
        const rawPositions = getRaw(`positions.${sportKey}`, {});
        if (!rawPositions || typeof rawPositions !== "object") return [];

        return Object.entries(rawPositions).map(([key, label]) => ({ key, label }));
    }, [getRaw, sportKey]);

    const handleStartEdit = (player) => {
        setEditingRosterId(player.id);
        setDraftShirtNumber(player.shirtNumber ?? "");
        setDraftPosition(player.position?.key || "");
    };

    const handleCancelEdit = () => {
        setEditingRosterId(null);
        setDraftShirtNumber("");
        setDraftPosition("");
    };

    const isNumeric = (value) => {
        if (value === null || value === undefined || value === "") return false;
        return !Number.isNaN(Number(value));
    };

    const handleSaveEdit = async (player) => {
        if (!activeProfileId) {
            toast.error(t("playersList.missingProfile"), {
                autoClose: 3000,
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        const jerseyNumber = `${draftShirtNumber}`.trim();
        const shirtNumber = isNumeric(jerseyNumber) ? parseInt(jerseyNumber, 10) : null;
        const position = draftPosition || null;

        if (shirtNumber !== null && (shirtNumber < 0 || !Number.isInteger(shirtNumber))) {
                toast.error(t("playersList.invalidShirtNumber"), {
                    autoClose: 3000,
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
        }

        const hasShirtChanged =
            shirtNumber !== null && Number(player.shirtNumber) !== Number(shirtNumber);
        const hasPositionChanged =
            !!position && position !== (player.position?.key || null);

        if (!hasShirtChanged && !hasPositionChanged) {
            handleCancelEdit();
            return;
        }

        const input = {
            profileId: Number(activeProfileId),
            rosterId: Number(player.id),
            shirtNumber,
            position,
            sportKey: sportKey,
        };

        try {
            setIsSaving(true);
            const { data } = await editPlayer({ variables: { input } });

            const savedRoster = data?.editPlayer?.roster || null;
            const savedShirtNumber = savedRoster?.shirtNumber ?? shirtNumber ?? player.shirtNumber;
            const savedPositionKey = savedRoster?.position?.key ?? position ?? player.position?.key;
            const savedPositionName =
                savedRoster?.position?.name ||
                t(`positions.${sportKey}.${savedPositionKey}`, { defaultValue: savedPositionKey });

            setPlayers((prev) =>
                prev.map((current) => {
                    if (current.id !== player.id) return current;

                    const nextPlayer = { ...current };
                    if (hasShirtChanged) nextPlayer.shirtNumber = savedShirtNumber;
                    if (savedPositionKey) {
                        nextPlayer.position = {
                            key: savedPositionKey,
                            name: savedPositionName,
                        };
                    }

                    return nextPlayer;
                })
            );

            if (onPlayerUpdated) {
                onPlayerUpdated(player.id, {
                    shirtNumber: savedShirtNumber,
                    position: savedPositionKey
                        ? { key: savedPositionKey, name: savedPositionName }
                        : player.position,
                });
            }

            toast.success(t("playersList.saveSuccess"), {
                autoClose: 2000,
                position: "top-center",
                closeOnClick: true,
            });
            handleCancelEdit();
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "playersList.saveError"), {
                autoClose: 3000,
                position: "top-center",
                closeOnClick: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (player) => {
        confirmDelete({
            title: t("playersList.deleteTitle"),
            message: t("playersList.deleteMessage", { name: player?.fullName || "" }),
            confirmLabel: t("playersList.deleteConfirm"),
            cancelLabel: t("common.cancel"),
            danger: true,
            onConfirm: async () => {
                try {
                    setDeletingRosterId(player.id);
                    const { data } = await deletePlayer({
                        variables: { rosterId: Number(player.id) },
                    });

                    if (!data?.deletePlayer?.success) {
                        toast.error(t("playersList.deleteError"), {
                            autoClose: 3000,
                            position: "top-center",
                            closeOnClick: true,
                        });
                        return;
                    }

                    setPlayers((prev) => prev.filter((item) => item.id !== player.id));
                    if (onPlayerDeleted) onPlayerDeleted(player.id);

                    toast.success(t("playersList.deleteSuccess"), {
                        autoClose: 2000,
                        position: "top-center",
                        closeOnClick: true,
                    });
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, "playersList.deleteError"), {
                        autoClose: 3000,
                        position: "top-center",
                        closeOnClick: true,
                    });
                } finally {
                    setDeletingRosterId(null);
                }
            },
        });
    };

    return (
        <>
            <div className="row">
                <div className="col-12 col-sm-6 col-md-12">
                    <div
                        className="d-flex align-items-center justify-content-between"
                        style={{
                            backgroundColor: colors.primary,
                            color: colors.textOnPrimary,
                            padding: "16px 20px",
                        }}
                    >
                        <div>
                            <h5 className="mb-0 fw-bold">
                                {t("common.players")}
                            </h5>
                            <small style={{ opacity: 0.85 }}>
                                {players.length}{" "}
                                {players.length === 1
                                    ? t("playersList.player")
                                    : t("common.players")}
                            </small>
                        </div>

                        <button
                            type="button"
                            className="btn"
                            onClick={onAddPlayer}
                            style={{color: colors.textOnSecondary, backgroundColor: colors.secondary}}
                        >
                            <i className="fas fa-plus mr-1" />
                            {t("teamMemberPicker.addPlayer")}
                        </button>
                    </div>
                    <div className="info-box">

                        {players.length ? (
                            <div className="card-body p-0">
                                <table className="table table-striped">
                                    <thead>
                                    <tr>
                                        <th>{t("playersList.player")}</th>
                                        <th>{t("playersList.jerseyNumber")}</th>
                                        <th>{t("playersList.position")}</th>
                                        <th>{t("playersList.actions")}</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {players.map((player) => {
                                        const isEditing = editingRosterId === player.id;
                                        const isDeleting = deletingRosterId === player.id;

                                        return (
                                        <tr key={player.id}>
                                            <td>
                                                <div
                                                    className="d-flex align-items-center"
                                                    style={{ gap: "10px", paddingTop: "10px" }}
                                                >
                                                    <InitialsAvatar
                                                        name={player.fullName}
                                                        size={50}
                                                        backgroundColor={colors.primary}
                                                        textColor={colors.textOnPrimary}
                                                    />

                                                    <div style={{ fontSize: "16px" }}>
                                                        {player.fullName}
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="form-control form-control-sm"
                                                        value={draftShirtNumber}
                                                        onChange={(e) => setDraftShirtNumber(e.target.value)}
                                                        disabled={isSaving || deletingRosterId !== null}
                                                    />
                                                ) : (
                                                    <div>
                                                        <span className="badge bg-kbola">
                                                            {player.shirtNumber}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                {isEditing ? (
                                                    <select
                                                        className="form-control form-control-sm"
                                                        value={draftPosition}
                                                        onChange={(e) => setDraftPosition(e.target.value)}
                                                        disabled={isSaving || deletingRosterId !== null}
                                                    >
                                                        <option value="">{t("playersList.choosePosition")}</option>
                                                        {positionOptions.map((position) => (
                                                            <option key={position.key} value={position.key}>
                                                                {position.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div>
                                                        <span className="badge bg-success">
                                                            {t(`positions.${division.sport?.key}.${player.position?.key}`, {defaultValue: player.position?.name})}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                {isEditing ? (
                                                    <div className="d-flex" style={{ gap: "6px" }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-kbola btn-sm"
                                                            onClick={() => handleSaveEdit(player)}
                                                            disabled={isSaving || deletingRosterId !== null}
                                                        >
                                                            {isSaving ? t("common.loading") : t("common.save")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-light btn-sm"
                                                            onClick={handleCancelEdit}
                                                            disabled={isSaving || deletingRosterId !== null}
                                                        >
                                                            {t("common.cancel")}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex" style={{ gap: "6px" }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handleStartEdit(player)}
                                                            disabled={isSaving || deletingRosterId !== null}
                                                        >
                                                            {t("common.edit")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => handleDelete(player)}
                                                            disabled={isSaving || deletingRosterId !== null || String(player.id).startsWith("tmp-")}
                                                            title={t("playersList.deleteConfirm")}
                                                            aria-label={t("playersList.deleteConfirm")}
                                                        >
                                                            {isDeleting ? (
                                                                <i className="fas fa-spinner fa-spin" />
                                                            ) : (
                                                                <i className="fas fa-trash" />
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <NoData />
                        )}

                    </div>
                </div>
            </div>
        </>
    );
}
